import { useDeferredValue, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  FilePlus2,
  FileText,
  Inbox,
  LoaderCircle,
  Mail,
  MailOpen,
  Paperclip,
  RefreshCw,
  Search,
  UserRound,
} from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import { InlineFieldInput } from '@/components/inline-field-group'
import { cn } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import { FileLink } from '../files/quote-files'
import { CustomerSearch } from '../start/customer-search'
import { Field } from '../start/address-panel'
import { formatRelativeTime } from '../ui/format'
import { AiProgress } from '../ui/ai-motion'
import { aiEnter, useArrivals } from '../ui/ai-motion-state'
import {
  canCreate,
  communicationsApi,
  communicationsKey,
  communicationsQueryOptions,
  isCreating,
  mailboxKey,
  mailboxQueryOptions,
  matchesThread,
  type Communication,
  type CommunicationAttachment,
  type CommunicationProgress,
  type InboxFilter,
} from './api'
import { SearchInput } from '@/components/search-input'

// The Communications inbox (USA-1032), ported from the demo's features/communications/inbox.tsx:
// the signed-in user's own inbox, a reading pane, and "Create quotes" for the selected threads.

const focus = 'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'
const statusLabels: Record<Communication['status'], string> = {
  idle: 'Not quoted',
  queued: 'Queued',
  running: 'Creating quote',
  complete: 'Quote ready',
  needs_review: 'Needs review',
  failed: 'Quote failed',
}
const filters: { value: InboxFilter; label: string }[] = [
  { value: 'all', label: 'All messages' },
  { value: 'unread', label: 'Unread' },
  { value: 'unquoted', label: 'Not quoted' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'needs_attention', label: 'Needs attention' },
  { value: 'complete', label: 'Quote ready' },
]
const REFRESH_MS = 60_000

function ThreadStatus({ thread }: { thread: Communication }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium',
        thread.status === 'complete'
          ? 'text-emerald-700 dark:text-emerald-400'
          : thread.status === 'failed'
            ? 'text-destructive'
            : thread.status === 'needs_review'
              ? 'text-amber-700 dark:text-amber-400'
              : isCreating(thread)
                ? 'text-violet-700 dark:text-violet-400'
                : 'text-muted-foreground',
      )}
    >
      {isCreating(thread) ? (
        <LoaderCircle aria-hidden="true" className="size-3 animate-spin motion-reduce:animate-none" />
      ) : thread.status === 'complete' ? (
        <Check aria-hidden="true" className="size-3" />
      ) : null}
      {statusLabels[thread.status]}
    </span>
  )
}

function QuoteLink({ thread, compact = false }: { thread: Communication; compact?: boolean }) {
  if (!thread.quote) return null
  return (
    <Link
      className={`inline-flex min-w-0 items-start gap-1 rounded text-xs font-semibold text-primary hover:underline ${focus}`}
      params={{ quoteId: thread.quote.id, stage: 'equipment' }}
      to="/quotes/$quoteId/workflow/$stage"
    >
      <span className="min-w-0 break-words [overflow-wrap:anywhere]">
        {thread.quote.quote_number}
        {!compact && ` · ${thread.quote.title || thread.subject}`}
      </span>
      <ArrowUpRight aria-hidden="true" className="mt-0.5 size-3 shrink-0" />
    </Link>
  )
}

/** An attachment still in the email: opens from the mailbox until a build saves it to the quote's Files. */
function MailAttachment({ threadId, attachment }: { threadId: string; attachment: CommunicationAttachment }) {
  const api = useQuoteBuilderApi()
  const open = useMutation({
    mutationFn: () => communicationsApi(api).attachment(threadId, attachment.id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    },
  })
  return (
    <span className="grid gap-0.5">
      <LoadingButton
        className="h-auto max-w-full justify-start px-2 py-1 font-medium"
        loading={open.isPending}
        loadingText={attachment.name}
        onClick={() => open.mutate()}
        size="sm"
        variant="outline"
      >
        <FileText aria-hidden="true" />
        <span className="min-w-0 truncate">{attachment.name}</span>
      </LoadingButton>
      {open.isError ? (
        <span className="px-1 text-xs text-destructive" role="alert">
          Could not open it. Try again.
        </span>
      ) : null}
    </span>
  )
}

/** Choose the customer a thread's quote is for: an existing customer, or a new one from the sender. */
function CustomerChooser({ thread, onDone }: { thread: Communication; onDone: () => void }) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [company, setCompany] = useState('')
  const [contact, setContact] = useState(thread.sender_name)
  const [email, setEmail] = useState(thread.sender_email)
  const choose = useMutation({
    mutationFn: async (customerId: number | null) => {
      const calls = communicationsApi(api)
      const id =
        customerId ??
        (await calls.createCustomer({ company_name: company.trim(), contact_name: contact.trim(), email: email.trim() })).id
      return calls.chooseCustomer(thread.id, id)
    },
    onSuccess: (updated) => {
      client.setQueryData<Communication[]>(communicationsKey, (previous) =>
        previous?.map((item) => (item.id === updated.id ? updated : item)),
      )
      onDone()
    },
  })
  return (
    <div className="mt-3 grid gap-3 rounded-lg border bg-muted/30 p-3">
      <div className="flex flex-wrap gap-1" role="tablist">
        {(['existing', 'new'] as const).map((value) => (
          <Button
            aria-selected={mode === value}
            key={value}
            onClick={() => setMode(value)}
            role="tab"
            size="sm"
            type="button"
            variant={mode === value ? 'secondary' : 'ghost'}
          >
            {value === 'existing' ? 'Existing customer' : 'New customer'}
          </Button>
        ))}
      </div>
      {mode === 'existing' ? (
        <CustomerSearch disabled={choose.isPending} onSelect={(customer) => choose.mutate(customer.id)} />
      ) : (
        <form
          className="grid gap-3 sm:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault()
            choose.mutate(null)
          }}
        >
          <Field label="Company">{(id) => <InlineFieldInput id={id} onChange={(event) => setCompany(event.target.value)} required value={company} />}</Field>
          <Field label="Contact">{(id) => <InlineFieldInput id={id} onChange={(event) => setContact(event.target.value)} required value={contact} />}</Field>
          <Field label="Email">{(id) => <InlineFieldInput id={id} onChange={(event) => setEmail(event.target.value)} required type="email" value={email} />}</Field>
          <div className="sm:col-span-3">
            <LoadingButton disabled={!company.trim()} loading={choose.isPending} loadingText="Adding…" size="sm" type="submit">
              Add customer
            </LoadingButton>
          </div>
        </form>
      )}
      {choose.isError ? (
        <p className="text-xs text-destructive" role="alert">
          Could not set the customer. Try again.
        </p>
      ) : null}
    </div>
  )
}

export function CommunicationsInbox() {
  const api = useQuoteBuilderApi()
  const calls = communicationsApi(api)
  const client = useQueryClient()
  const { thread: activeId } = useSearch({ strict: false }) as { thread?: string }
  const navigate = useNavigate()
  const setActiveId = (thread: string | undefined) => {
    void navigate({ to: '/communications', search: { thread }, replace: true, resetScroll: false })
  }
  const inbox = useQuery(communicationsQueryOptions(api))
  const mailbox = useQuery(mailboxQueryOptions(api))
  const [search, setSearch] = useState('')
  const deferredSearch = useDeferredValue(search)
  const [filter, setFilter] = useState<InboxFilter>('all')
  const [selected, setSelected] = useState<string[]>([])
  const [mobileReader, setMobileReader] = useState(Boolean(activeId))
  const [choosingCustomer, setChoosingCustomer] = useState<string | null>(null)
  const [activity, setActivity] = useState<Record<string, CommunicationProgress>>({})
  const readerHeading = useRef<HTMLHeadingElement>(null)
  const listHeading = useRef<HTMLHeadingElement>(null)
  const threadButtons = useRef(new Map<string, HTMLButtonElement>())

  const refresh = useMutation({
    mutationFn: () => calls.refresh(),
    onSuccess: (status) => client.setQueryData(mailboxKey, status),
  })
  const { mutate: refreshMailbox } = refresh
  useEffect(() => {
    // Read new mail from Outlook on open and every minute while the inbox is visible.
    refreshMailbox()
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') refreshMailbox()
    }, REFRESH_MS)
    return () => clearInterval(timer)
  }, [refreshMailbox])

  // Live progress streams only while a build is queued or running; the list refresh covers the rest.
  const building = (inbox.data || []).some(isCreating)
  useEffect(() => {
    if (!building) return
    const controller = new AbortController()
    let reconnect: ReturnType<typeof setTimeout>
    const connect = async () => {
      try {
        await communicationsApi(api).stream((updates) => {
          setActivity(Object.fromEntries(updates.map((update) => [update.id, update])))
          client.setQueryData<Communication[]>(communicationsKey, (previous) =>
            previous?.map((thread) => {
              const update = updates.find((item) => item.id === thread.id)
              return update ? { ...thread, status: update.status, progress: update.progress, error: update.error } : thread
            }),
          )
        }, controller.signal)
      } catch {
        /* Saved-state polling stays available while the stream reconnects. */
      }
      if (!controller.signal.aborted) reconnect = setTimeout(() => void connect(), 2000)
    }
    void connect()
    return () => {
      controller.abort()
      clearTimeout(reconnect)
    }
  }, [api, client, building])

  const updateThreads = async (updated: Communication[]) => {
    await client.cancelQueries({ queryKey: communicationsKey })
    client.setQueryData<Communication[]>(communicationsKey, (previous = []) =>
      previous.map((thread) => updated.find((item) => item.id === thread.id) || thread),
    )
  }
  const autoRead = useMutation({
    mutationFn: (id: string) => calls.markRead([id], true),
    onSuccess: updateThreads,
    onSettled: () => void client.invalidateQueries({ queryKey: communicationsKey }),
  })
  const mutation = useMutation({
    mutationFn: async (action: { ids: string[]; kind: 'create' | 'read' | 'unread' }) => {
      await client.cancelQueries({ queryKey: communicationsKey })
      return action.kind === 'create' ? calls.createQuotes(action.ids) : calls.markRead(action.ids, action.kind === 'read')
    },
    onSuccess: async (updated, action) => {
      await updateThreads(updated)
      if (action.kind === 'create') {
        if (!activeId) setActiveId(updated[0]?.id)
        void client.invalidateQueries({ queryKey: ['quotes'] })
      }
    },
    onSettled: () => void client.invalidateQueries({ queryKey: communicationsKey }),
  })

  const threads = inbox.data || []
  const visible = threads.filter((thread) => matchesThread(thread, deferredSearch, filter))
  const active = threads.find((thread) => thread.id === activeId)
  const allSteps = (active ? activity[active.id]?.steps || [] : []).filter(
    (step, index, steps) => steps.lastIndexOf(step) === index,
  )
  const activitySteps = allSteps.slice(-6)
  // Track the full history: the visible window stays at six while new steps keep arriving.
  const arrivedSteps = useArrivals(allSteps)
  const selectedThreads = threads.filter((thread) => selected.includes(thread.id))
  const creatable = selectedThreads.filter(canCreate)
  const allSelected = visible.length > 0 && visible.every((thread) => selected.includes(thread.id))
  const someSelected = visible.some((thread) => selected.includes(thread.id))
  const hiddenSelected = selectedThreads.filter((thread) => !visible.includes(thread)).length
  const creating = mutation.isPending && mutation.variables?.kind === 'create'
  const act = (ids: string[], kind: 'create' | 'read' | 'unread') => {
    if (ids.length && !mutation.isPending) mutation.mutate({ ids, kind })
  }
  const openThread = (thread: Communication) => {
    setActiveId(thread.id)
    setMobileReader(true)
    setChoosingCustomer(null)
    if (!thread.is_read) autoRead.mutate(thread.id)
    requestAnimationFrame(() => readerHeading.current?.focus())
  }
  const backToList = () => {
    setMobileReader(false)
    requestAnimationFrame(() => {
      const target = (activeId ? threadButtons.current.get(activeId) : null) || listHeading.current
      target?.focus()
    })
  }
  const syncedAt = mailbox.data?.synced_at
  const mailboxProblem = mailbox.data && !mailbox.data.available
    ? 'Email is not connected in this environment.'
    : mailbox.data?.error
      ? 'Could not read your inbox. Refresh to try again.'
      : null

  return (
    <section
      aria-label="Communications inbox"
      className="flex min-h-[560px] w-full flex-col overflow-hidden rounded-xl border bg-card md:h-[calc(100dvh-11rem)]"
    >
      <p aria-atomic="true" className="sr-only" role="status">
        {threads.filter(isCreating).length} quotes in progress. {threads.filter((thread) => thread.status === 'complete').length}{' '}
        quotes ready. {threads.filter((thread) => thread.status === 'failed' || thread.status === 'needs_review').length} need
        attention.
      </p>
      <div className={cn('flex flex-wrap items-center gap-3 border-b px-4 py-3', mobileReader && 'hidden md:flex')}>
        <h2 className={`mr-auto rounded text-base font-semibold ${focus}`} ref={listHeading} tabIndex={-1}>
          Inbox{' '}
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            {threads.filter((thread) => !thread.is_read).length} unread
            {syncedAt ? ` · updated ${formatRelativeTime(syncedAt)}` : ''}
          </span>
        </h2>
        <label className="relative order-last w-full sm:order-none sm:w-80">
          <span className="sr-only">Search messages</span>
          <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <SearchInput
            className="pl-9"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search messages and attachments"
            type="search"
            value={search}
          />
        </label>
        <Button
          aria-label="Refresh inbox"
          disabled={refresh.isPending}
          onClick={() => {
            refresh.mutate()
            void inbox.refetch()
          }}
          size="icon"
          type="button"
          variant="ghost"
        >
          <RefreshCw aria-hidden="true" className={cn('size-4', (refresh.isPending || inbox.isFetching) && 'animate-spin motion-reduce:animate-none')} />
        </Button>
      </div>

      <div className={cn('flex flex-wrap items-center gap-2 border-b bg-muted/40 px-3 py-2', mobileReader && 'hidden md:flex')}>
        <label className="flex min-h-9 items-center gap-2 px-1 text-xs text-muted-foreground">
          <input
            aria-label="Select all visible messages"
            checked={allSelected}
            className={`size-4 accent-primary ${focus}`}
            disabled={!visible.length}
            onChange={() =>
              setSelected((previous) =>
                allSelected
                  ? previous.filter((id) => !visible.some((thread) => thread.id === id))
                  : [...new Set([...previous, ...visible.map((thread) => thread.id)])],
              )
            }
            ref={(element) => {
              if (element) element.indeterminate = someSelected && !allSelected
            }}
            type="checkbox"
          />
          <span>{selectedThreads.length ? `${selectedThreads.length} selected` : 'Select all'}</span>
        </label>
        {selectedThreads.length ? (
          <Button onClick={() => setSelected([])} size="sm" type="button" variant="ghost">
            Clear selection
          </Button>
        ) : null}
        {hiddenSelected ? <span className="text-xs text-muted-foreground">{hiddenSelected} outside this filter</span> : null}
        <div className="flex flex-wrap items-center gap-1 md:ml-auto">
          <Button
            disabled={mutation.isPending || !selectedThreads.some((thread) => !thread.is_read)}
            onClick={() => act(selected, 'read')}
            size="sm"
            type="button"
            variant="ghost"
          >
            <MailOpen aria-hidden="true" />
            Mark read
          </Button>
          <Button
            disabled={mutation.isPending || !selectedThreads.some((thread) => thread.is_read)}
            onClick={() => act(selected, 'unread')}
            size="sm"
            type="button"
            variant="ghost"
          >
            <Mail aria-hidden="true" />
            Mark unread
          </Button>
          <LoadingButton
            disabled={mutation.isPending || !creatable.length}
            loading={creating}
            loadingText="Starting…"
            onClick={() => act(creatable.map((thread) => thread.id), 'create')}
            size="sm"
            type="button"
          >
            <FilePlus2 aria-hidden="true" />
            {creatable.length > 1 ? `Create ${creatable.length} quotes` : 'Create quote'}
          </LoadingButton>
        </div>
      </div>

      {mailboxProblem ? (
        <p className="border-b px-4 py-3 text-xs text-destructive" role="alert">
          {mailboxProblem}
        </p>
      ) : null}
      {mutation.isError ? (
        <p className="border-b px-4 py-3 text-xs text-destructive" role="alert">
          {mutation.variables?.kind === 'create'
            ? 'Could not confirm the quote request. Refresh the inbox, then retry any unfinished messages.'
            : 'Could not change read status. Try again.'}
        </p>
      ) : null}
      {autoRead.isError ? (
        <p className="border-b px-4 py-3 text-xs text-destructive" role="alert">
          Could not mark the message read. Use Mark read to try again.
        </p>
      ) : null}
      {inbox.isError ? (
        <div className="flex items-center gap-2 border-b px-4 py-3 text-xs text-destructive" role="alert">
          {inbox.data ? 'Updates paused. Displayed progress may be out of date.' : 'Could not load messages.'}
          <Button onClick={() => void inbox.refetch()} size="sm" type="button" variant="outline">
            Retry
          </Button>
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 md:grid-cols-[minmax(280px,36%)_minmax(0,1fr)]">
        <div className={cn('min-w-0 overflow-y-auto md:border-r', mobileReader && 'hidden md:block')}>
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-card px-4 py-2">
            <label className="min-w-0 flex-1">
              <span className="sr-only">Filter messages</span>
              <select
                className={`min-h-9 max-w-full rounded bg-card pr-2 text-xs font-semibold ${focus}`}
                onChange={(event) => setFilter(event.target.value as InboxFilter)}
                value={filter}
              >
                {filters.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground" role="status">
              {visible.length} {visible.length === 1 ? 'message' : 'messages'}
            </span>
          </div>
          {inbox.isPending ? (
            <p className="p-6 text-xs text-muted-foreground" role="status">
              Loading messages…
            </p>
          ) : null}
          {!inbox.isPending && !inbox.isError && !visible.length ? (
            <div className="p-6 text-center">
              <Inbox aria-hidden="true" className="mx-auto mb-3 size-6 text-muted-foreground" />
              <p className="text-sm font-semibold">{threads.length ? 'No matching messages' : 'Your inbox is empty'}</p>
              {threads.length ? (
                <Button
                  className="mt-3"
                  onClick={() => {
                    setSearch('')
                    setFilter('all')
                  }}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  Clear filters
                </Button>
              ) : null}
            </div>
          ) : null}
          <ul aria-label="Messages" className="divide-y">
            {visible.map((thread) => (
              <li
                className={cn(
                  'px-3 py-3',
                  thread.id === activeId ? 'bg-muted' : selected.includes(thread.id) ? 'bg-muted/50' : 'hover:bg-muted/50',
                )}
                key={thread.id}
              >
                <div className="flex items-start gap-2">
                  <label className="flex min-h-8 w-6 shrink-0 items-center justify-center">
                    <span className="sr-only">Select {thread.subject}</span>
                    <input
                      checked={selected.includes(thread.id)}
                      className={`size-4 accent-primary ${focus}`}
                      onChange={() =>
                        setSelected((previous) =>
                          previous.includes(thread.id) ? previous.filter((id) => id !== thread.id) : [...previous, thread.id],
                        )
                      }
                      type="checkbox"
                    />
                  </label>
                  <div className="min-w-0 flex-1">
                    <button
                      aria-current={thread.id === activeId ? 'true' : undefined}
                      aria-label={`Open ${thread.subject}`}
                      className={`block w-full rounded text-left ${focus}`}
                      onClick={() => openThread(thread)}
                      ref={(element) => {
                        if (element) threadButtons.current.set(thread.id, element)
                        else threadButtons.current.delete(thread.id)
                      }}
                      type="button"
                    >
                      <span className="flex items-start gap-2">
                        <span className={cn('min-w-0 flex-1 break-words text-xs [overflow-wrap:anywhere]', !thread.is_read && 'font-bold')}>
                          {thread.sender_name}
                        </span>
                        <time
                          className="shrink-0 pt-0.5 text-[11px] text-muted-foreground"
                          dateTime={thread.received_at}
                          title={new Date(thread.received_at).toLocaleString()}
                        >
                          {new Date(thread.received_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </time>
                      </span>
                      <span
                        className={cn(
                          'mt-1 block break-words text-sm leading-5 [overflow-wrap:anywhere]',
                          !thread.is_read ? 'font-semibold text-foreground' : 'text-muted-foreground',
                        )}
                      >
                        {!thread.is_read ? (
                          <span className="mr-1.5 inline-block size-1.5 rounded-full bg-primary align-middle">
                            <span className="sr-only">Unread: </span>
                          </span>
                        ) : null}
                        {thread.subject}
                      </span>
                      <span className="mt-1 line-clamp-2 break-words text-xs leading-5 text-muted-foreground [overflow-wrap:anywhere]">
                        {thread.messages.at(-1)?.body}
                      </span>
                    </button>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <ThreadStatus thread={thread} />
                      {thread.attachments.length ? (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Paperclip aria-hidden="true" className="size-3" />
                          {thread.attachments.length}
                          <span className="sr-only"> attachments</span>
                        </span>
                      ) : null}
                    </div>
                    {isCreating(thread) ? <AiProgress className="mt-2 rounded-full" from={0.05} seconds={240} to={0.92} /> : null}
                    {isCreating(thread) && thread.progress ? (
                      <p className={cn('mt-1 break-words text-xs leading-4 text-muted-foreground [overflow-wrap:anywhere]', aiEnter)} key={thread.progress}>
                        {thread.progress}
                      </p>
                    ) : null}
                    {thread.quote ? (
                      <div className="mt-1.5">
                        <QuoteLink compact thread={thread} />
                      </div>
                    ) : null}
                    {thread.status === 'failed' ? (
                      <Button
                        aria-label={`Retry quote for ${thread.subject}`}
                        className="mt-2 h-7 px-2"
                        disabled={mutation.isPending}
                        onClick={() => act([thread.id], 'create')}
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        Retry quote
                      </Button>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <section aria-label="Reading pane" className={cn('min-w-0 overflow-y-auto', !mobileReader && 'hidden md:block')}>
          {active ? (
            <>
              <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2 md:px-6">
                <Button className="md:hidden" onClick={backToList} size="sm" type="button" variant="ghost">
                  <ArrowLeft aria-hidden="true" />
                  Inbox
                </Button>
                <Button
                  className="ml-auto"
                  disabled={mutation.isPending}
                  onClick={() => act([active.id], active.is_read ? 'unread' : 'read')}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  {active.is_read ? 'Mark unread' : 'Mark read'}
                </Button>
                {active.status === 'idle' || active.status === 'failed' ? (
                  <LoadingButton
                    disabled={mutation.isPending || !canCreate(active)}
                    loading={creating && mutation.variables?.ids.includes(active.id)}
                    loadingText="Starting…"
                    onClick={() => act([active.id], 'create')}
                    size="sm"
                    type="button"
                  >
                    {active.status === 'failed' ? 'Retry quote' : 'Create quote'}
                  </LoadingButton>
                ) : null}
              </div>
              <div className="border-b px-4 py-5 md:px-6">
                <h3 className={`break-words rounded text-lg font-semibold leading-7 [overflow-wrap:anywhere] ${focus}`} ref={readerHeading} tabIndex={-1}>
                  {active.subject}
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <UserRound aria-hidden="true" className="size-4 text-muted-foreground" />
                  {active.customer_uuid ? (
                    <Link
                      className={`break-words font-medium text-primary hover:underline [overflow-wrap:anywhere] ${focus}`}
                      params={{ groupId: active.customer_uuid }}
                      to="/customers/$groupId"
                    >
                      {active.customer_name}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">No customer</span>
                  )}
                  {!active.quote ? (
                    <Button
                      className="h-7 px-2"
                      onClick={() => setChoosingCustomer(choosingCustomer === active.id ? null : active.id)}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      {active.customer_id ? 'Change' : 'Choose customer'}
                    </Button>
                  ) : null}
                </div>
                {choosingCustomer === active.id && !active.quote ? (
                  <CustomerChooser onDone={() => setChoosingCustomer(null)} thread={active} />
                ) : null}
                {active.status !== 'idle' ? (
                  <div aria-live="polite" className="mt-4 flex flex-col items-start gap-2 border-t pt-3">
                    <ThreadStatus thread={active} />
                    {isCreating(active) ? <AiProgress className="w-full rounded-full" from={0.05} seconds={240} to={0.92} /> : null}
                    {active.progress && active.status !== 'complete' ? (
                      <p className={cn('break-words text-xs leading-5 text-muted-foreground [overflow-wrap:anywhere]', aiEnter)} key={active.progress}>
                        {active.progress}
                      </p>
                    ) : null}
                    {activitySteps.length ? (
                      <ol aria-label="Quote activity" className="w-full space-y-1.5 text-xs leading-5">
                        {activitySteps.map((step) => (
                          <li className={cn('flex items-start gap-2', arrivedSteps.has(step) && aiEnter)} key={step}>
                            <Check aria-hidden="true" className="mt-1 size-3 shrink-0 text-emerald-700 dark:text-emerald-400" />
                            <span className="line-clamp-2 min-w-0 break-words [overflow-wrap:anywhere]">{step.replace('financing', 'payment terms')}</span>
                          </li>
                        ))}
                      </ol>
                    ) : null}
                    <QuoteLink thread={active} />
                  </div>
                ) : null}
                {active.attachments.length ? (
                  <ul aria-label="Attachments" className="mt-4 flex flex-wrap gap-2">
                    {active.attachments.map((attachment) => (
                      <li className="max-w-full" key={`${attachment.message_id}-${attachment.id}`}>
                        {attachment.file ? (
                          <FileLink file={attachment.file} small />
                        ) : (
                          <MailAttachment attachment={attachment} threadId={active.id} />
                        )}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div className="divide-y">
                {active.messages.map((message, index) => (
                  <article className="px-4 py-5 md:px-6" key={`${active.id}-${index}`}>
                    <header className="mb-4 flex flex-wrap items-start gap-x-4 gap-y-1">
                      <div className="min-w-0 flex-1">
                        <h4 className="break-words text-sm font-semibold [overflow-wrap:anywhere]">{message.sender_name}</h4>
                        <p className="mt-0.5 break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">{message.sender_email}</p>
                      </div>
                      <time className="text-xs text-muted-foreground" dateTime={message.sent_at}>
                        {new Date(message.sent_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                      </time>
                    </header>
                    <div className="max-w-[72ch] whitespace-pre-wrap break-words text-sm leading-6 [overflow-wrap:anywhere]">{message.body}</div>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <div className="flex h-full min-h-80 flex-col items-center justify-center px-6 text-center">
              <MailOpen aria-hidden="true" className="mb-3 size-7 text-muted-foreground" />
              <h3 className="text-sm font-semibold">Select a message to read</h3>
              <p className="mt-1 text-xs text-muted-foreground">Use the checkboxes to create a quote for each selected message.</p>
            </div>
          )}
        </section>
      </div>
    </section>
  )
}
